import '../mutators/setGetMeetingTypeExtensionRequestStatusMutator';

import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import { getGuid } from 'owa-guid';
import { makeGetRequest } from 'owa-ows-gateway';

import setGetMeetingTypeExtensionRequestStatus from '../actions/setGetMeetingTypeExtensionRequestStatus';
import getEntryPoint from '../selectors/getEntryPoint';
import type ServicesRequestStatus from '../store/schema/ServicesRequestStatus';
import store from '../store/Store';
import { getUserMailboxGuid } from '../utils/getMailboxGuid';
import getMailboxPuid from '../selectors/getMailboxPuid';

import type MeetingTypeExtensionResponse from './schema/MeetingTypeExtensionResponse';
import type ResponseError from './schema/ResponseError';

export default async function getMeetingTypeExtension(
    itemId: string
): Promise<MeetingTypeExtensionResponse | null> {
    const perfDatapoint = new PerformanceDatapoint('getMeetingTypeExtension');
    perfDatapoint.addCustomData({
        meetingTypeId: itemId,
        entryPoint: getEntryPoint(),
        mailboxGuid: getUserMailboxGuid(),
        bookingMailboxPuid: getMailboxPuid(),
    });
    try {
        const url = `ows/beta/BookWithMe/GetMeetingTypeExtension?itemid=${itemId}`;
        /* eslint-disable-next-line owa-custom-rules/require-mailboxInfoInOwsRequest  -- (https://aka.ms/OWALintWiki)
         * MailboxInfo must be providded to OWS calls, see https://aka.ms/multiaccountlinter
         *	> All OWS calls must pass in a MailboxInfo or OwsRequestOptions obtained via getOwsMailboxRequestOptions. */
        return await makeGetRequest<MeetingTypeExtensionResponse>(
            url /* requestUrl */,
            getGuid() /* correlationId */,
            false /* returnFullResponse */,
            undefined /* customHeaders */,
            true /* throwServiceError */,
            undefined /* includeCredentials */,
            'GetMeetingTypeExtension' /* actionName */
        ).then(
            (response: MeetingTypeExtensionResponse) => {
                setGetMeetingTypeExtensionRequestStatus(2);
                perfDatapoint.end();
                return response;
            },
            (error: ResponseError) => {
                if (error.message?.endsWith('http error:404') || error.status == 404) {
                    // Notfound 404 is expected when the initial PUBLISHING_SETTINGS_ID MTE has not been created, not an error.
                    setGetMeetingTypeExtensionRequestStatus(5);
                    perfDatapoint.end();
                } else if (error.message?.endsWith('http error:403') || error.status == 403) {
                    // 403 is expected when the tenant admin disabled OpenBook, show the form that has the info "Contact to your IT department."
                    setGetMeetingTypeExtensionRequestStatus(3);
                    perfDatapoint.endWithError(DatapointStatus.UserError, error);
                } else {
                    // Check if there is already a saved getMeetingTypeExtensionRequestStatus with value ErrorAccessDenied, from the call to get all meeting type extensions, if so that's the one to use.
                    const getMeetingTypeExtensionRequestStatus =
                        store.pbpPublishingSetting.getMeetingTypeExtensionRequestStatus;
                    if (getMeetingTypeExtensionRequestStatus != 3) {
                        // 500 errors from OWS are unexpected and will need to trigger some alerts based on threshold.
                        setGetMeetingTypeExtensionRequestStatus(4);
                        perfDatapoint.endWithError(DatapointStatus.ServerError, error);
                    } else {
                        // Keep the getMeetingTypeExtensionRequestStatus with access denied and set the perfdatapoint with user error.
                        setGetMeetingTypeExtensionRequestStatus(3);
                        perfDatapoint.endWithError(DatapointStatus.UserError, error);
                    }
                }

                return Promise.reject(error);
            }
        );
    } catch (ex) {
        if (!perfDatapoint.hasEnded) {
            perfDatapoint.endWithError(DatapointStatus.ServerError, ex);
        }
        return null;
    }
}
